import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const num = (v) => Number(v || 0);

const HOW = { AUTO: 'Matched automatically', MANUAL: 'Matched by hand', ENTRY: 'Entry made here', RECEIPT: 'Receipt recorded here', PAYMENT: 'Payment recorded here' };
const FILTERS = [['UNMATCHED', 'To do'], ['MATCHED', 'Matched'], ['IGNORED', 'Set aside'], ['', 'All']];
const COLUMN_NAMES = { DATE: 'Date', DESCRIPTION: 'Narration', REFERENCE: 'Reference', IN: 'Money in', OUT: 'Money out', AMOUNT: 'Amount', TYPE: 'Dr/Cr', BALANCE: 'Balance' };

/**
 * Accounts > Bank Reconciliation: upload a bank account's statement as the bank gives it (Excel or CSV), match its
 * lines to the books (most match on their own), record what the books don't have yet, and see the reconciliation
 * statement: the book balance, what hasn't cleared yet, and any difference from the bank's balance.
 */
function BankReconciliation({ active, currency = 'INR', canRecordReceipt }) {
  const [accounts, setAccounts] = useState(null);
  const [ledgerId, setLedgerId] = useState('');
  const [summary, setSummary] = useState(null);
  const [lines, setLines] = useState([]);
  const [statements, setStatements] = useState([]);
  const [filter, setFilter] = useState('UNMATCHED');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [recording, setRecording] = useState(null);
  const [showNotInBank, setShowNotInBank] = useState(false);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 });
    return (v) => f.format(num(v));
  }, [currency]);

  useEffect(() => {
    if (!active || accounts) return;
    axios.get('/api/bank-reconciliation/accounts')
      .then((r) => {
        setAccounts(r.data || []);
        if ((r.data || []).length > 0) setLedgerId(String(r.data[0].ledgerId));
      })
      .catch((error) => toast.error(errorMessage(error, 'Could not load the bank accounts')));
  }, [active, accounts]);

  const load = useCallback(async () => {
    if (!ledgerId) return;
    setLoading(true);
    try {
      const [s, l, st] = await Promise.all([
        axios.get(`/api/bank-reconciliation/${ledgerId}/summary`),
        axios.get(`/api/bank-reconciliation/${ledgerId}/lines`),
        axios.get(`/api/bank-reconciliation/${ledgerId}/statements`)
      ]);
      setSummary(s.data);
      setLines(l.data || []);
      setStatements(st.data || []);
      if (!s.data.hasStatement) setShowUpload(true);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load the reconciliation'));
    } finally {
      setLoading(false);
    }
  }, [ledgerId]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const refreshAccounts = () => axios.get('/api/bank-reconciliation/accounts').then((r) => setAccounts(r.data || [])).catch(() => {});
  const changed = () => { load(); refreshAccounts(); };

  const act = async (key, request, success) => {
    setBusy(key);
    try {
      await request();
      if (success) toast.success(success);
      changed();
      return true;
    } catch (error) {
      toast.error(errorMessage(error, 'That did not work'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const match = (line, posting) => act(`m${line.id}`, () => axios.post(`/api/bank-reconciliation/lines/${line.id}/match`, { transactionId: posting.id }), 'Matched');
  const reopen = (line) => {
    if (line.matchedHow === 'ENTRY' && !window.confirm('Undo the entry made from this line? A reversing entry is posted.')) return;
    act(`r${line.id}`, () => axios.post(`/api/bank-reconciliation/lines/${line.id}/reopen`),
      line.matchedHow === 'RECEIPT' || line.matchedHow === 'PAYMENT'
        ? 'Unmatched. The receipt or payment stays; cancel it where it is if it was wrong.'
        : line.matchedHow === 'ENTRY' ? 'Entry undone' : 'Back to do');
  };
  const autoMatch = () => act('auto', async () => {
    const { data } = await axios.post(`/api/bank-reconciliation/${ledgerId}/auto-match`);
    toast.info(data.matched > 0 ? `Matched ${data.matched} more line${data.matched === 1 ? '' : 's'}` : 'Nothing more matches on its own');
  });
  const removeStatement = (s) => {
    if (!window.confirm(`Remove ${s.fileName || 'this statement'} and its ${s.lines} lines? Matches on them are dropped.`)) return;
    act(`s${s.id}`, () => axios.delete(`/api/bank-reconciliation/statements/${s.id}`), 'Statement removed');
  };

  if (!active) return null;
  if (accounts === null) return <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>;
  if (accounts.length === 0) {
    return <Alert variant="info">Add a bank account under Accounts &rsaquo; Bank Accounts first; then upload its statement here.</Alert>;
  }

  const shown = lines.filter((l) => !filter || l.status === filter);
  const counts = lines.reduce((c, l) => ({ ...c, [l.status]: (c[l.status] || 0) + 1 }), {});

  return (
    <div>
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <Form.Group style={{ minWidth: '16rem' }}>
          <Form.Label className="small text-muted mb-1">Bank account</Form.Label>
          <Form.Select value={ledgerId} onChange={(e) => { setLedgerId(e.target.value); setSummary(null); setLines([]); setShowUpload(false); }}>
            {accounts.map((a) => (
              <option key={a.ledgerId} value={a.ledgerId}>
                {a.name}{a.accountEnding ? ` ··${a.accountEnding}` : ''}{a.toDo > 0 ? ` (${a.toDo} to do)` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Button variant={showUpload ? 'outline-secondary' : 'primary'} onClick={() => setShowUpload((v) => !v)}>
          <i className="bi bi-upload me-1" />{showUpload ? 'Close upload' : 'Upload statement'}
        </Button>
        {summary?.hasStatement && (
          <Button variant="outline-primary" onClick={autoMatch} disabled={busy === 'auto'}>
            {busy === 'auto' ? 'Matching...' : 'Match again'}
          </Button>
        )}
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      {showUpload && <StatementUpload ledgerId={ledgerId} money={money} onDone={() => { setShowUpload(false); setFilter('UNMATCHED'); changed(); }} />}

      {summary && !summary.hasStatement && !showUpload && (
        <Alert variant="info">No statement yet for this account. Upload one to start.</Alert>
      )}

      {summary?.hasStatement && (
        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Reconciliation summary={summary} money={money} onShowNotInBank={() => setShowNotInBank((v) => !v)} showingNotInBank={showNotInBank} />
          </Col>
          <Col lg={6}>
            {showNotInBank && (
              <Card className="mb-3">
                <Card.Header className="py-2"><strong>In your books, not in the bank yet</strong></Card.Header>
                <Card.Body className="p-0">
                  {summary.notInBank.length === 0 ? <p className="text-muted small p-3 mb-0">Nothing: everything in the books has cleared.</p> : (
                    <Table size="sm" className="small mb-0">
                      <thead><tr><th>Date</th><th>Entry</th><th className="text-end">In</th><th className="text-end">Out</th></tr></thead>
                      <tbody>
                        {summary.notInBank.map((p) => (
                          <tr key={p.id}>
                            <td className="text-nowrap">{day(p.date)}</td>
                            <td>{p.reference && <span className="font-monospace me-1">{p.reference}</span>}{p.description}</td>
                            <td className="text-end">{p.in ? money(p.amount) : ''}</td>
                            <td className="text-end">{p.in ? '' : money(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            )}
            {statements.length > 0 && (
              <Card>
                <Card.Header className="py-2"><strong>Statements uploaded</strong></Card.Header>
                <Card.Body className="p-0">
                  <Table size="sm" className="small mb-0">
                    <tbody>
                      {statements.map((s) => (
                        <tr key={s.id}>
                          <td>{s.fileName || 'Statement'}<div className="text-muted">{day(s.from)} to {day(s.to)} · {s.lines} lines · {s.by}</div></td>
                          <td className="text-end align-middle">
                            <Button size="sm" variant="link" className="p-0 text-danger" disabled={busy === `s${s.id}`} onClick={() => removeStatement(s)}>Remove</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      )}

      {lines.length > 0 && (
        <>
          <Nav variant="pills" className="mb-2" activeKey={filter} onSelect={(k) => setFilter(k === 'ALL' ? '' : k)}>
            {FILTERS.map(([key, label]) => (
              <Nav.Item key={label}>
                <Nav.Link eventKey={key || 'ALL'} active={filter === key} className="py-1 px-3">
                  {label}{key && counts[key] ? ` (${counts[key]})` : ''}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <Table bordered hover responsive size="sm" className="small">
            <thead>
              <tr>
                <th style={{ width: '7rem' }}>Date</th><th>Narration</th>
                <th className="text-end" style={{ width: '8rem' }}>Out</th><th className="text-end" style={{ width: '8rem' }}>In</th>
                <th style={{ width: '34%' }}>In your books</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-3">
                  {filter === 'UNMATCHED' ? 'All done: every line is matched or set aside.' : 'No lines here.'}
                </td></tr>
              )}
              {shown.map((l) => (
                <tr key={l.id}>
                  <td className="text-nowrap">{day(l.date)}</td>
                  <td style={{ wordBreak: 'break-word' }}>
                    {l.description}
                    {l.reference && <div className="text-muted font-monospace">{l.reference}</div>}
                  </td>
                  <td className="text-end text-nowrap">{num(l.out) > 0 ? money(l.out) : ''}</td>
                  <td className="text-end text-nowrap">{num(l.in) > 0 ? money(l.in) : ''}</td>
                  <td>
                    {l.status === 'MATCHED' && (
                      <div className="d-flex justify-content-between gap-2">
                        <div>
                          {l.posting ? <><span className="font-monospace me-1">{l.posting.reference}</span>{l.posting.description}</> : 'Matched'}
                          <div className="text-muted">{HOW[l.matchedHow] || 'Matched'}{l.posting ? `, ${day(l.posting.date)}` : ''}</div>
                        </div>
                        <Button size="sm" variant="link" className="p-0 text-nowrap" disabled={busy === `r${l.id}`} onClick={() => reopen(l)}>
                          {l.matchedHow === 'ENTRY' ? 'Undo entry' : 'Unmatch'}
                        </Button>
                      </div>
                    )}
                    {l.status === 'IGNORED' && (
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Set aside{l.note ? `: ${l.note}` : ''}</span>
                        {l.matchedHow !== 'BEFORE_OPENING' && (
                          <Button size="sm" variant="link" className="p-0 text-nowrap" disabled={busy === `r${l.id}`} onClick={() => reopen(l)}>Bring back</Button>
                        )}
                      </div>
                    )}
                    {l.status === 'UNMATCHED' && (
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        {(l.suggestions || []).map((p) => (
                          <Button key={p.id} size="sm" variant="outline-success" className="py-0 text-start" disabled={busy === `m${l.id}`}
                            title={p.description} onClick={() => match(l, p)}>
                            Match {p.reference || p.description} · {day(p.date)}
                          </Button>
                        ))}
                        <Button size="sm" variant="outline-primary" className="py-0" onClick={() => setRecording(l)}>
                          {(l.suggestions || []).length > 0 ? 'Other…' : 'Record…'}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {recording && (
        <RecordLine line={recording} ledgerId={ledgerId} money={money} canRecordReceipt={canRecordReceipt}
          onHide={() => setRecording(null)} onDone={() => { setRecording(null); changed(); }} />
      )}
    </div>
  );
}

function Reconciliation({ summary: s, money, onShowNotInBank, showingNotInBank }) {
  const diff = s.difference == null ? null : num(s.difference);
  const row = (label, value, sign, extra) => (
    <tr>
      <td>{label}{extra}</td>
      <td className="text-end text-nowrap">{sign}{money(Math.abs(num(value)))}</td>
    </tr>
  );
  return (
    <Card>
      <Card.Header className="py-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <strong>Reconciliation as on {day(s.asOf)}</strong>
        <span className="small text-muted">Statement {day(s.statementFrom)} to {day(s.statementTo)}</span>
      </Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" className="small mb-0">
          <tbody>
            {row('Balance in your books', s.bookBalance, num(s.bookBalance) < 0 ? '-' : '')}
            {num(s.depositsNotCredited) > 0 && row('Less: deposits the bank hasn’t credited yet', s.depositsNotCredited, '- ')}
            {num(s.paymentsNotDebited) > 0 && row('Add: payments the bank hasn’t paid out yet (cheques)', s.paymentsNotDebited, '+ ')}
            {num(s.creditsNotInBooks) > 0 && row('Add: money in on the statement, not in your books yet', s.creditsNotInBooks, '+ ')}
            {num(s.debitsNotInBooks) > 0 && row('Less: money out on the statement, not in your books yet', s.debitsNotInBooks, '- ')}
            <tr className="fw-semibold">
              <td>Should be the bank&apos;s balance</td>
              <td className="text-end text-nowrap">{num(s.expectedBankBalance) < 0 ? '-' : ''}{money(Math.abs(num(s.expectedBankBalance)))}</td>
            </tr>
            {s.bankBalance != null ? (
              <>
                {row(`Bank’s balance on ${day(s.bankBalanceDate)}`, s.bankBalance, num(s.bankBalance) < 0 ? '-' : '')}
                <tr className={diff === 0 ? 'table-success' : 'table-warning'}>
                  <td>
                    {diff !== 0 ? 'Difference to look into' : s.toDo === 0
                      ? <><i className="bi bi-check-circle me-1" />Reconciled</>
                      : `Agrees with the bank once the ${s.toDo} line${s.toDo === 1 ? '' : 's'} to do ${s.toDo === 1 ? 'is' : 'are'} recorded`}
                    {diff !== 0 && (
                      <div className="text-muted">
                        Usually an entry dated before the first statement line, a missing statement, or a wrong amount in the books.
                      </div>
                    )}
                  </td>
                  <td className="text-end text-nowrap fw-semibold">{diff === 0 ? '' : money(diff)}</td>
                </tr>
              </>
            ) : (
              <tr><td colSpan={2} className="text-muted">The statement has no balance column, so it can&apos;t be checked against the bank&apos;s balance.</td></tr>
            )}
          </tbody>
        </Table>
        <div className="d-flex flex-wrap gap-3 px-2 py-2 border-top small">
          <span><Badge bg={s.toDo > 0 ? 'warning' : 'success'} text={s.toDo > 0 ? 'dark' : undefined}>{s.toDo}</Badge> to do</span>
          <span><Badge bg="secondary">{s.matched}</Badge> matched</span>
          {s.notInBank.length > 0 && (
            <Button size="sm" variant="link" className="p-0 ms-auto" onClick={onShowNotInBank}>
              {showingNotInBank ? 'Hide' : 'See'} the {s.notInBank.length} entr{s.notInBank.length === 1 ? 'y' : 'ies'} not in the bank yet
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function StatementUpload({ ledgerId, money, onDone }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (path) => {
    const body = new FormData();
    body.append('file', file);
    return (await axios.post(`/api/bank-reconciliation/${ledgerId}/${path}`, body)).data;
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      setPreview(await send('statements/preview'));
    } catch (error) {
      setPreview(null);
      toast.error(errorMessage(error, 'Could not read the statement'));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const r = await send('statements');
      toast.success(`Added ${r.imported} line${r.imported === 1 ? '' : 's'}; ${r.matched} matched on their own${r.toDo > 0 ? `, ${r.toDo} to do` : ''}.`);
      if (fileInput.current) fileInput.current.value = '';
      onDone();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not add the statement'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <p className="small text-muted mb-2">
          Download the statement from your bank&apos;s net banking as Excel or CSV and upload it as it is. Lines already uploaded are skipped,
          so an overlapping period is fine.
        </p>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Form.Control ref={fileInput} type="file" accept=".xls,.xlsx,.csv,.txt" style={{ maxWidth: '24rem' }}
            onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); }} />
          <Button variant="outline-primary" onClick={doPreview} disabled={!file || busy}>
            {busy && !preview ? <Spinner animation="border" size="sm" /> : 'Preview'}
          </Button>
        </div>
        {preview && (
          <div className="mt-3">
            <div className="d-flex flex-wrap gap-3 align-items-center mb-2 small">
              <span><strong>{day(preview.from)}</strong> to <strong>{day(preview.to)}</strong></span>
              <Badge bg="success">{preview.newCount} new</Badge>
              {preview.alreadyCount > 0 && <Badge bg="secondary">{preview.alreadyCount} already here</Badge>}
              <span>In {money(preview.totalIn)} · Out {money(preview.totalOut)}</span>
              {preview.closingBalance != null && <span>Closing balance {money(preview.closingBalance)}</span>}
              <Button className="ms-auto" onClick={doImport} disabled={busy || preview.newCount === 0}>
                {busy ? 'Adding...' : `Add ${preview.newCount} line${preview.newCount === 1 ? '' : 's'}`}
              </Button>
            </div>
            <div className="small text-muted mb-2">
              Read from the file: {Object.entries(preview.columns || {}).map(([k, v]) => `${COLUMN_NAMES[k] || k} ← “${v}”`).join(', ')}
            </div>
            <Table size="sm" bordered responsive className="small mb-0">
              <thead><tr><th>Date</th><th>Narration</th><th className="text-end">Out</th><th className="text-end">In</th><th className="text-end">Balance</th></tr></thead>
              <tbody>
                {preview.rows.slice(0, 15).map((r) => (
                  <tr key={r.line} className={r.already ? 'text-muted' : ''}>
                    <td className="text-nowrap">{day(r.date)}</td>
                    <td>{r.description}{r.already && <Badge bg="secondary" className="ms-2">already here</Badge>}</td>
                    <td className="text-end">{num(r.out) > 0 ? money(r.out) : ''}</td>
                    <td className="text-end">{num(r.in) > 0 ? money(r.in) : ''}</td>
                    <td className="text-end">{r.balance != null ? money(r.balance) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {preview.total > 15 && <div className="small text-muted mt-1">Showing 15 of {preview.total} lines.</div>}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/** What to do with a line the books don't have: match it to an entry, record it, or set it aside. */
function RecordLine({ line, ledgerId, money, canRecordReceipt, onHide, onDone }) {
  const isIn = num(line.in) > 0;
  const amount = isIn ? line.in : line.out;
  const [candidates, setCandidates] = useState(null);
  const [choice, setChoice] = useState('');
  const [parties, setParties] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [otherLedgerId, setOtherLedgerId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [settleShortfall, setSettleShortfall] = useState(false);
  const [saving, setSaving] = useState(false);

  const options = useMemo(() => {
    const o = [['MATCH', 'An entry already in the books']];
    if (isIn) {
      if (canRecordReceipt) o.push(['RECEIPT', 'Receipt from a customer']);
      o.push(['INTEREST_RECEIVED', 'Interest received']);
    } else {
      o.push(['PAYMENT', 'Payment to a vendor'], ['BANK_CHARGES', 'Bank charges'], ['INTEREST_PAID', 'Interest paid']);
    }
    o.push(['OTHER', 'Another account (cash, expense, transfer, loan...)'], ['IGNORE', 'Set it aside: the books need nothing for it']);
    return o;
  }, [isIn, canRecordReceipt]);

  useEffect(() => {
    axios.get(`/api/bank-reconciliation/lines/${line.id}/candidates`)
      .then((r) => {
        setCandidates(r.data || []);
        if ((r.data || []).length > 0) {
          setChoice('MATCH');
          setTransactionId(String(r.data[0].id));
        } else {
          const charge = !isIn && /CHG|CHRG|CHARGE|FEE|GST|SMS|AMC/i.test(line.description || '');
          setChoice(charge ? 'BANK_CHARGES' : isIn ? (canRecordReceipt ? 'RECEIPT' : 'OTHER') : 'PAYMENT');
        }
      })
      .catch(() => setCandidates([]));
    axios.get('/api/accounts/ledgers')
      .then((r) => setLedgers((r.data || []).filter((l) => l.isActive !== false && String(l.id) !== String(ledgerId) && !l.customer && !l.vendor)))
      .catch(() => {});
  }, [line, ledgerId, isIn, canRecordReceipt]);

  useEffect(() => {
    setPartyId('');
    if (choice === 'RECEIPT') {
      axios.get('/api/customers').then((r) => setParties((Array.isArray(r.data) ? r.data : []).sort((a, b) => (a.name || '').localeCompare(b.name || '')))).catch(() => setParties([]));
    } else if (choice === 'PAYMENT') {
      axios.get('/api/inventory/vendors/active').then((r) => setParties((Array.isArray(r.data) ? r.data : []).sort((a, b) => (a.name || '').localeCompare(b.name || '')))).catch(() => setParties([]));
    }
  }, [choice]);

  const save = async () => {
    const base = `/api/bank-reconciliation/lines/${line.id}`;
    let request;
    if (choice === 'MATCH') {
      if (!transactionId) { toast.warn('Choose the entry'); return; }
      request = () => axios.post(`${base}/match`, { transactionId: Number(transactionId) });
    } else if (choice === 'RECEIPT') {
      if (!partyId) { toast.warn('Choose the customer'); return; }
      request = () => axios.post(`${base}/receipt`, { customerId: Number(partyId), settleShortfall });
    } else if (choice === 'PAYMENT') {
      if (!partyId) { toast.warn('Choose the vendor'); return; }
      request = () => axios.post(`${base}/vendor-payment`, { vendorId: Number(partyId) });
    } else if (choice === 'OTHER') {
      if (!otherLedgerId) { toast.warn('Choose the account'); return; }
      request = () => axios.post(`${base}/entry`, { ledgerId: Number(otherLedgerId), note });
    } else if (choice === 'IGNORE') {
      request = () => axios.post(`${base}/ignore`, { note });
    } else {
      request = () => axios.post(`${base}/entry`, { kind: choice, note });
    }
    setSaving(true);
    try {
      await request();
      toast.success(choice === 'IGNORE' ? 'Set aside' : choice === 'MATCH' ? 'Matched' : 'Recorded and matched');
      onDone();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not record it'));
    } finally {
      setSaving(false);
    }
  };

  const byType = ledgers.reduce((g, l) => ({ ...g, [l.type || 'OTHER']: [...(g[l.type || 'OTHER'] || []), l] }), {});

  return (
    <Modal show onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isIn ? 'Money in' : 'Money out'}: {money(amount)} on {day(line.date)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3" style={{ wordBreak: 'break-word' }}>{line.description}{line.reference ? ` · ${line.reference}` : ''}</div>
        <Form.Label className="fw-semibold">What is it?</Form.Label>
        {options.map(([key, label]) => (
          <Form.Check key={key} type="radio" id={`rec-${key}`} name="rec-choice" label={label} checked={choice === key}
            onChange={() => setChoice(key)} className="mb-1" />
        ))}
        <div className="mt-3">
          {choice === 'MATCH' && (candidates === null ? <Spinner animation="border" size="sm" /> : candidates.length === 0 ? (
            <Alert variant="light" className="small mb-0">Nothing in the books for exactly {money(amount)} {isIn ? 'in' : 'out'} that isn&apos;t matched already. Record it instead.</Alert>
          ) : (
            <Form.Select value={transactionId} onChange={(e) => setTransactionId(e.target.value)}>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>{day(p.date)} · {p.reference ? `${p.reference} · ` : ''}{p.description}</option>
              ))}
            </Form.Select>
          ))}
          {(choice === 'RECEIPT' || choice === 'PAYMENT') && (
            <>
              <Form.Select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
                <option value="">Choose the {choice === 'RECEIPT' ? 'customer' : 'vendor'}</option>
                {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Form.Select>
              <Form.Text className="text-muted">
                {choice === 'RECEIPT'
                  ? 'Recorded as a receipt on this date. It settles their oldest unpaid bills first; any rest stays as an advance.'
                  : 'Recorded as a payment on this date, on account and without TDS. If TDS applies, record it under Vendor Payments and match it here.'}
              </Form.Text>
              {choice === 'RECEIPT' && (
                <Form.Check
                  className="mt-2"
                  id="settle-shortfall"
                  type="checkbox"
                  checked={settleShortfall}
                  onChange={(e) => setSettleShortfall(e.target.checked)}
                  label="If the money falls short of a bill by 1 or less, settle the difference as a discount allowed"
                />
              )}
            </>
          )}
          {choice === 'OTHER' && (
            <Form.Select value={otherLedgerId} onChange={(e) => setOtherLedgerId(e.target.value)}>
              <option value="">Choose the account</option>
              {Object.entries(byType).map(([type, list]) => (
                <optgroup key={type} label={type.charAt(0) + type.slice(1).toLowerCase()}>
                  {list.map((l) => <option key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ''}</option>)}
                </optgroup>
              ))}
            </Form.Select>
          )}
          {['OTHER', 'BANK_CHARGES', 'INTEREST_RECEIVED', 'INTEREST_PAID', 'IGNORE'].includes(choice) && (
            <Form.Control className="mt-2" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
              placeholder={choice === 'IGNORE' ? 'Why (optional)' : 'Description (optional; the narration is used otherwise)'} />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button onClick={save} disabled={saving || !choice}>{saving ? 'Saving...' : choice === 'IGNORE' ? 'Set aside' : choice === 'MATCH' ? 'Match' : 'Record'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BankReconciliation;
